import React, { useState } from 'react';

interface MemorialStyle {
  id: number;
  title: string;
  description: string;
  imageSrc: string;
  points?: string[];
  demoUrl: string;
}

const memorialStyles: MemorialStyle[] = [
  {
    id: 1,
    title: 'Classic Elegance',
    description: 'A timeless design with elegant typography and golden accents, perfect for creating a dignified memorial.',
    imageSrc: '/images/CE-Mobile.svg',
    points: [
      'Elegant typography',
      'Rich accent elements',
      'Traditional layout'
    ],
    demoUrl: 'https://classic-elegance.digitalmemorials.ie/'
  },
  {
    id: 2,
    title: 'Modern Minimalist',
    description: 'Clean lines and minimalist design focusing on photos and memories, creating a sophisticated tribute.',
    imageSrc: '/images/MM-Mobile.svg',
    points: [
      'Clean minimal design',
      'Focus on photography',
      'Contemporary feel'
    ],
    demoUrl: 'https://modern-minimalist.digitalmemorials.ie/'
  },
  {
    id: 3,
    title: 'Traditional Ornate',
    description: 'Rich in traditional elements and ornate details, this style honors cherished memories with memorial aesthetics.',
    imageSrc: '/images/TO-Mobile.svg',
    points: [
      'Ornate details',
      'Classic elements',
      'Formal presentation'
    ],
    demoUrl: 'https://www.traditional-ornate.digitalmemorials.ie/'
  },
  {
    id: 4,
    title: 'Contemporary Bold',
    description: 'A bold and modern approach that makes a statement while maintaining respectful commemoration.',
    imageSrc: '/images/CB-Mobile.svg',
    points: [
      'Bold design elements',
      'Modern aesthetics',
      'Dynamic layout'
    ],
    demoUrl: 'https://contemporary-bold.digitalmemorials.ie/'
  },
  {
    id: 5,
    title: 'Serene Natural',
    description: 'A peaceful design incorporating natural elements and calming aesthetics for a tranquil memorial experience.',
    imageSrc: '/images/SN-Mobile.svg',
    points: [
      'Natural elements',
      'Serene aesthetics',
      'Organic patterns'
    ],
    demoUrl: 'https://serene-natural.digitalmemorials.ie/'
  }
];

const MemorialStylesShowcase: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () => {
    setActiveIndex((current) => 
      current === memorialStyles.length - 1 ? 0 : current + 1
    );
  };

  const prevSlide = () => {
    setActiveIndex((current) => 
      current === 0 ? memorialStyles.length - 1 : current - 1
    );
  };

  return (
    <div className="relative py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 relative">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
            <span className="text-white">Beautiful</span>
            <span className="bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text">
              {' '}Memorial Styles
            </span>
          </h2>
          <p className="mt-4 text-[#FFFFFFCC] max-w-2xl mx-auto">
            Choose from our carefully crafted designs, each created to honor and celebrate your loved one's memory.
          </p>
        </div>

        {/* Desktop Layout - Hidden on Mobile */}
        <div className="relative hidden lg:block">
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 z-10 p-3 rounded-full bg-[#1A1A1A] border border-white/10 transition-all duration-200 group hover:bg-primary/20 hover:border-primary/50"
          >
            <svg className="w-6 h-6 text-white group-hover:text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 z-10 p-3 rounded-full bg-[#1A1A1A] border border-white/10 transition-all duration-200 group hover:bg-primary/20 hover:border-primary/50"
          >
            <svg className="w-6 h-6 text-white group-hover:text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Desktop Cards Container */}
          <div className="relative h-[500px] mx-12">
            {memorialStyles.map((style, index) => (
              <div
                key={style.id}
                className="memorial-card absolute inset-0 bg-gradient-to-br from-[#1A1A1A] via-[#1C1C1C] to-[#141414] rounded-2xl overflow-hidden transform transition-all duration-500 ease-out"
                style={{
                  opacity: index === activeIndex ? 1 : 0,
                  transform: `scale(${index === activeIndex ? 1 : 0.95})`,
                  zIndex: index === activeIndex ? 1 : 0,
                  pointerEvents: index === activeIndex ? 'auto' : 'none',
                }}
              >
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-primary/20 to-transparent opacity-30 blur-3xl" />
                <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-primary/10 to-transparent opacity-20 blur-3xl" />
                <div 
                  className="absolute inset-0 opacity-30"
                  style={{
                    backgroundImage: `radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)`,
                    backgroundSize: '100% 100%'
                  }}
                />

                {/* Card Content */}
                <div className="relative h-full">
                  <div className="absolute inset-0 p-10 flex">
                    {/* Left Content */}
                    <div className="flex-1 pr-12 flex flex-col relative">
                      {/* Title Section */}
                      <div className="mb-8">
                        <h3 className="text-3xl font-bold text-white inline-block font-montserrat tracking-wide">
                          {style.title}
                        </h3>
                        <div className="h-0.5 w-24 bg-gradient-to-r from-primary via-[#E6C45C] to-transparent mt-3" />
                      </div>
                      
                      {/* Description */}
                      <p className="text-[#FFFFFFCC] mb-8 text-lg leading-relaxed">
                        {style.description}
                      </p>

                      {/* Points Section */}
                      <div className="space-y-4 backdrop-blur-sm bg-[#1A1A1A]/30 rounded-xl p-6 border border-white/5">
                        <h4 className="text-primary/90 font-semibold mb-2 font-montserrat">Key Features</h4>
                        {style.points?.map((point, i) => (
                          <div key={i} className="flex items-center gap-4 group">
                            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-primary to-[#E6C45C] group-hover:scale-150 transition-transform duration-300" />
                            <span className="text-[#FFFFFFCC] text-base group-hover:text-white transition-colors duration-300">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Link Section */}
                      <div className="mt-auto pt-8">
                        <a
                          href={style.demoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex items-center gap-3 text-primary hover:text-white transition-all duration-300"
                        >
                          <span className="text-lg font-medium relative">
                            View Demo
                            <div className="absolute inset-x-0 -bottom-0.5 h-px bg-gradient-to-r from-primary to-[#E6C45C] scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
                          </span>
                          <svg 
                            className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>

                    </div>

                    {/* Right Content - Image */}
                    <div className="w-[45%] relative rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.3)] border border-white/10 flex items-center justify-center">
                      {/* Image Overlay Gradients */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-transparent opacity-60 mix-blend-overlay" />
                      <img
                        src={style.imageSrc}
                        alt={`${style.title} Memorial Style Preview`}
                        className="w-[400px] transform scale-[2] translate-x-[12%] translate-y-[7%]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-70" />
                      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 to-transparent" />
                      
                      {/* Decorative Corner Elements */}
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/20 to-transparent opacity-50 transform rotate-45" />
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-primary/20 to-transparent opacity-50 transform -rotate-45" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Progress Indicators */}
          <div className="flex justify-center gap-3 mt-12">
            {memorialStyles.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`transition-all duration-300 rounded-full border border-primary/30 hover:border-primary/60
                  ${index === activeIndex 
                    ? 'w-12 h-3 bg-gradient-to-r from-primary to-[#E6C45C]' 
                    : 'w-3 h-3 bg-[#1A1A1A] hover:bg-primary/20'}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Tablet and Mobile Layout */}
        <div className="lg:hidden">
          <div className="relative px-4">
            {memorialStyles.map((style, index) => (
              <div
                key={style.id}
                className={`mb-8 ${index === activeIndex ? 'block' : 'hidden'}`}
              >
                {/* Card */}
                <div className="bg-gradient-to-br from-[#1A1A1A] via-[#1C1C1C] to-[#141414] rounded-xl overflow-hidden relative">
                  {/* Decorative Elements - Added to both layouts */}
                  <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-primary/20 to-transparent opacity-30 blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-primary/10 to-transparent opacity-20 blur-3xl" />
                  <div 
                    className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage: `radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)`,
                      backgroundSize: '100% 100%'
                    }}
                  />

                  {/* Tablet Layout (md to lg) */}
                  <div className="hidden md:lg:flex flex-row relative">
                    {/* Content Section */}
                    <div className="flex-1 p-8">
                      <h3 className="text-2xl font-bold text-white font-montserrat tracking-wide mb-2">
                        {style.title}
                      </h3>
                      <div className="h-0.5 w-16 bg-gradient-to-r from-primary to-[#E6C45C]/50 mb-4" />
                      
                      <p className="text-[#FFFFFFCC] text-base leading-relaxed mb-6">
                        {style.description}
                      </p>

                      {/* Features List */}
                      <div className="space-y-3 mb-6 bg-[#1A1A1A]/30 rounded-lg p-4 border border-white/5">
                        <h4 className="text-primary/90 font-semibold font-montserrat">Key Features</h4>
                        {style.points?.map((point, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-primary to-[#E6C45C]" />
                            <span className="text-[#FFFFFFCC]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Demo Link */}
                      <a
                        href={style.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-[#E6C45C] text-[#0A0A0A] rounded-lg font-semibold transition-opacity duration-200 hover:opacity-90"
                      >
                        View Demo
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>

                    {/* Image Section for Tablet */}
                    <div className="w-[45%] relative rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.3)] border border-white/10 flex items-center justify-center">
                      {/* Image Overlay Gradients */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-transparent opacity-60 mix-blend-overlay" />
                      <img
                        src={style.imageSrc}
                        alt={`${style.title} Memorial Style Preview`}
                        className="w-[400px] transform scale-[1.8] translate-x-[12%] translate-y-[7%]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-70" />
                      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 to-transparent" />
                      
                      {/* Decorative Corner Elements */}
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/20 to-transparent opacity-50 transform rotate-45" />
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-primary/20 to-transparent opacity-50 transform -rotate-45" />
                    </div>
                  </div>

                  {/* Mobile Layout (below md) */}
                  <div className="md:lg:hidden relative">
                    {/* Image Section */}
                    <div className="relative aspect-[4/3] overflow-hidden border-b border-white/10 rounded-t-xl">
                      {/* Image Container with Shadow and Border */}
                      <div className="absolute inset-0 shadow-[0_0_50px_rgba(0,0,0,0.3)] border border-white/10 overflow-hidden">
                        {/* Image Overlay Gradients */}
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-transparent opacity-60 mix-blend-overlay" />
                        <img
                          src={style.imageSrc}
                          alt={`${style.title} Memorial Style Preview`}
                          className="w-full h-full object-cover transform scale-[1.2] translate-x-[8%] translate-y-[5%]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-70" />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 to-transparent" />
                        
                        {/* Decorative Corner Elements */}
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/20 to-transparent opacity-50 transform rotate-45" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-primary/20 to-transparent opacity-50 transform -rotate-45" />
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-6 relative">
                      <h3 className="text-2xl font-bold text-white font-montserrat tracking-wide mb-2">
                        {style.title}
                      </h3>
                      <div className="h-0.5 w-16 bg-gradient-to-r from-primary to-[#E6C45C]/50 mb-4" />
                      
                      <p className="text-[#FFFFFFCC] text-base leading-relaxed mb-6">
                        {style.description}
                      </p>

                      {/* Features List */}
                      <div className="space-y-3 mb-6 bg-[#1A1A1A]/30 rounded-lg p-4 border border-white/5">
                        <h4 className="text-primary/90 font-semibold font-montserrat text-sm">Key Features</h4>
                        {style.points?.map((point, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-primary to-[#E6C45C]" />
                            <span className="text-[#FFFFFFCC] text-sm">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Demo Link */}
                      <a
                        href={style.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary to-[#E6C45C] text-[#0A0A0A] rounded-lg font-semibold transition-opacity duration-200 hover:opacity-90"
                      >
                        View Demo
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Controls - unchanged */}
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={prevSlide}
                className="p-2 rounded-lg bg-[#1A1A1A] border border-white/10 text-white hover:text-primary transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Dots Indicator */}
              <div className="flex gap-2">
                {memorialStyles.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`transition-all duration-300 h-2 rounded-full border border-primary/30
                      ${index === activeIndex 
                        ? 'w-8 bg-gradient-to-r from-primary to-[#E6C45C]' 
                        : 'w-2 bg-[#1A1A1A] hover:bg-primary/20'}`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                className="p-2 rounded-lg bg-[#1A1A1A] border border-white/10 text-white hover:text-primary transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemorialStylesShowcase;